import { useState } from "react";
import { ApiError, issuesOf } from "../../../lib/api";
import { api } from "../../../lib/api";
import { ConfirmButton, Issues, UpIcon, XIcon, move } from "../fields";
import {
  type OutlineRow,
  applyOutline,
  isBlankDeck,
  isEmptySection,
  newSectionId,
  outlineRows,
  planFromRows,
} from "../model";
import type { StudioCtx } from "./ctx";

const MAX_SECTIONS = 20;

/**
 * Step 2: the talk's sections, before any slide. Each has a title, minutes, a purpose (for the AI and you) and up to
 * four key points (on its section slide). The AI can suggest the whole outline or change it; nothing is saved until
 * Save, and saving keeps the slides of every section that stays.
 */
export function OutlineStep({ ctx }: { ctx: StudioCtx }) {
  const { deck, settings } = ctx;
  const saved = outlineRows(deck, settings.plan);
  const [rows, setRows] = useState<OutlineRow[]>(saved);
  const [suggested, setSuggested] = useState<{ before: OutlineRow[] } | null>(null);
  const [gen, setGen] = useState(0); // bumped when the rows are replaced wholesale, so their fields start fresh
  const [ask, setAsk] = useState("");
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [issues, setIssues] = useState<{ path: string; message: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const aiOn = ctx.ai?.configured !== false;

  const dirty = JSON.stringify(rows) !== JSON.stringify(saved);
  const planned = rows.reduce((n, r) => n + (r.minutes || 0), 0);
  const target = settings.brief.minutes;
  const { dropped } = applyOutline(deck, rows);
  const problems = [
    ...(rows.length === 0 ? ["Add at least one section."] : []),
    ...rows.flatMap((r, i) => [
      ...(!r.title.trim() ? [`Section ${i + 1} needs a title.`] : []),
      ...(!(r.minutes >= 1 && r.minutes <= 120) ? [`Section ${i + 1}: minutes from 1 to 120.`] : []),
    ]),
  ];

  const setRow = (i: number, patch: Partial<OutlineRow>) =>
    setRows((list) => list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((list) => [
      ...list,
      { id: newSectionId(`Section ${list.length + 1}`, list, deck), title: "", minutes: 5, points: [], intent: "" },
    ]);

  const suggest = async () => {
    setAsking(true);
    setAskError(null);
    try {
      const r = await api.suggestOutline(
        ctx.deckId,
        ask,
        rows.length ? rows.map((row) => ({ ...row, title: row.title || "Untitled" })) : undefined,
      );
      setSuggested({ before: rows });
      setGen((g) => g + 1);
      setRows(
        r.sections.map((s) => ({ id: s.id, title: s.title, minutes: s.minutes, points: s.points, intent: s.intent })),
      );
      setAsk("");
    } catch (e) {
      const found = issuesOf(e);
      const text = e instanceof ApiError || e instanceof Error ? e.message : String(e);
      setAskError(
        found.length
          ? `${text}: ${found
              .slice(0, 2)
              .map((i) => i.message)
              .join("; ")}`
          : text,
      );
    }
    setAsking(false);
  };

  /** Saves the outline (a version, plus the plan in the settings). Resolves to whether it saved. */
  const save = async (): Promise<boolean> => {
    if (problems.length) return false;
    if (!dirty) return true;
    setSaving(true);
    const { deck: next } = applyOutline(deck, rows);
    const found = await ctx.commit(next, `Outline: ${rows.length} section${rows.length === 1 ? "" : "s"}`);
    setSaving(false);
    setIssues(found);
    if (found.length) return false;
    ctx.saveSettings({ plan: planFromRows(rows, settings.plan) });
    setSuggested(null);
    return true;
  };

  const anyWritten = deck.sections.some((s) => !isEmptySection(s));
  const saveLabel = dropped.length
    ? `Save, removing ${dropped.length} section${dropped.length === 1 ? "" : "s"} with slides`
    : "Save outline";

  return (
    <div className="step-page">
      <header className="step-head">
        <p className="step-eyebrow">Step 2 of 4</p>
        <h1 className="step-h">Plan the outline</h1>
        <p className="step-lead">
          The talk’s sections, in order. Give each one a job: the AI writes its slides from that, and the key points
          appear on the section’s title slide.
        </p>
      </header>

      <section className="step-card ai-card" aria-labelledby="outline-ai">
        <h2 id="outline-ai" className="step-card-h">
          {rows.length ? "Ask AI to change the outline" : "Let AI suggest an outline"}
        </h2>
        <p className="step-card-lead">
          {rows.length
            ? "Say what to change, or leave it empty for a better version. You’ll see the result before anything is saved."
            : "From your basics and notes. You can change everything afterwards, or write the outline yourself below."}
        </p>
        <div className="row-gap">
          <input
            className="input"
            aria-label="What should the outline do?"
            value={ask}
            maxLength={1000}
            disabled={!aiOn}
            placeholder={
              rows.length
                ? "e.g. add a live demo before the wrap-up, cut the history"
                : "e.g. start with a story from our incident in May"
            }
            onChange={(e) => setAsk(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !asking && aiOn && void suggest()}
          />
          <button
            type="button"
            className="s-btn s-btn-ink s-btn-tall"
            disabled={asking || !aiOn}
            onClick={() => void suggest()}
          >
            {asking ? "Thinking…" : rows.length ? "Change outline" : "Suggest outline"}
          </button>
        </div>
        {asking && <span className="busy">PLANNING THE SECTIONS · A FEW SECONDS</span>}
        {!aiOn && (
          <p className="banner banner-grey">
            AI is off: set OPENAI_API_KEY for the server. You can still write the outline yourself.
          </p>
        )}
        {askError && (
          <p className="banner banner-yellow" role="alert">
            {askError}
          </p>
        )}
      </section>

      {suggested && (
        <p className="banner banner-yellow undo-bar" role="status">
          Suggested by AI. Edit anything below; nothing is saved until you save the outline.
          <button
            type="button"
            className="s-btn s-btn-small"
            onClick={() => {
              setRows(suggested.before);
              setGen((g) => g + 1);
              setSuggested(null);
            }}
          >
            Undo
          </button>
        </p>
      )}

      <section className="step-card" aria-labelledby="outline-sections">
        <div className="step-card-head">
          <h2 id="outline-sections" className="step-card-h">
            Sections
          </h2>
          <MinutesMeter planned={planned} target={target} />
        </div>
        <Issues issues={issues} />
        {rows.length === 0 ? (
          <div className="empty-state">
            <p>No sections yet. Ask the AI above, or start writing them yourself.</p>
            <button type="button" className="s-btn" onClick={addRow}>
              + Add the first section
            </button>
          </div>
        ) : (
          <ol className="outline-rows">
            {rows.map((r, i) => {
              const existing = deck.sections.find((s) => s.id === r.id);
              const slides = existing && !isEmptySection(existing) ? existing.blocks.length : 0;
              return (
                <li key={`${gen}:${r.id}`} className="outline-row">
                  <span className="outline-num">{String(i + 1).padStart(2, "0")}</span>
                  <div className="outline-fields">
                    <div className="outline-line">
                      <input
                        className="input outline-title"
                        aria-label={`Section ${i + 1} title`}
                        placeholder="Section title"
                        maxLength={80}
                        value={r.title}
                        onChange={(e) => setRow(i, { title: e.target.value })}
                      />
                      <label className="outline-minutes">
                        <input
                          className="input input-mono"
                          type="number"
                          min={1}
                          max={120}
                          aria-label={`Section ${i + 1} minutes`}
                          value={r.minutes || ""}
                          onChange={(e) => setRow(i, { minutes: Number(e.target.value) || 0 })}
                        />
                        min
                      </label>
                    </div>
                    <input
                      className="input"
                      aria-label={`Section ${i + 1} purpose`}
                      placeholder="Its job, in one sentence: what the audience should get from it"
                      maxLength={200}
                      value={r.intent}
                      onChange={(e) => setRow(i, { intent: e.target.value })}
                    />
                    <PointsField
                      label={`Section ${i + 1} key points`}
                      value={r.points}
                      onChange={(points) => setRow(i, { points })}
                    />
                    {slides > 0 && (
                      <span className="field-hint">
                        {slides} slide{slides === 1 ? "" : "s"} written · kept when you save
                      </span>
                    )}
                  </div>
                  <div className="outline-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Move section ${i + 1} up`}
                      disabled={i === 0}
                      onClick={() => setRows((list) => move(list, i, i - 1))}
                    >
                      <UpIcon />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Move section ${i + 1} down`}
                      disabled={i === rows.length - 1}
                      onClick={() => setRows((list) => move(list, i, i + 1))}
                    >
                      <UpIcon down />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Remove section ${i + 1}`}
                      onClick={() => setRows((list) => list.filter((_, j) => j !== i))}
                    >
                      <XIcon />
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {rows.length > 0 && rows.length < MAX_SECTIONS && (
          <button type="button" className="s-btn s-btn-dashed" style={{ alignSelf: "flex-start" }} onClick={addRow}>
            + Section
          </button>
        )}
        {problems.length > 0 && rows.length > 0 && (
          <ul className="issues" role="status">
            {problems.slice(0, 3).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </section>

      <footer className="step-foot">
        <button type="button" className="s-btn s-btn-tall" onClick={() => ctx.goto("basics")}>
          Back
        </button>
        <span className="spacer" />
        {dirty && (
          <SaveButton
            label={saveLabel}
            confirm={dropped.length > 0}
            disabled={saving || problems.length > 0}
            onSave={() => void save()}
          />
        )}
        {(!isBlankDeck(deck) || rows.length > 0) && (
          <button
            type="button"
            className="s-btn s-btn-ink s-btn-tall"
            disabled={saving || problems.length > 0 || dropped.length > 0}
            title={dropped.length ? "Save first: this outline removes sections with slides" : undefined}
            onClick={async () => (await save()) && ctx.goto("slides")}
          >
            {dirty ? "Save and build the slides" : "Next: build the slides"}
          </button>
        )}
      </footer>

      {rows.length > 0 && (
        <section className="step-card step-card-quiet" aria-labelledby="outline-all">
          <h2 id="outline-all" className="step-card-h">
            Prefer one go?
          </h2>
          <p className="step-card-lead">
            Save the outline and let the AI write every section’s slides at once. You can still go through them one by
            one afterwards.
          </p>
          <div className="row-gap">
            <ConfirmButton
              className="s-btn"
              label={ctx.busy === "deck" ? "Writing every slide…" : "Draft all slides now"}
              confirmLabel={anyWritten ? "Replace the slides already written?" : "Draft every section?"}
              disabled={!aiOn || ctx.busy !== null || saving || problems.length > 0 || dropped.length > 0}
              onConfirm={async () => {
                if (!(await save())) return;
                if (await ctx.generateAll(true)) ctx.goto("slides");
              }}
            />
          </div>
          {ctx.busy === "deck" && <span className="busy">WRITING THE DECK · USUALLY UNDER A MINUTE</span>}
          {ctx.aiError?.where === "deck" && (
            <div className="banner banner-yellow" role="alert">
              {ctx.aiError.message}
              {ctx.aiError.issues.length > 0 && (
                <ul className="issues">
                  {ctx.aiError.issues.slice(0, 6).map((i, n) => (
                    <li key={n}>
                      {i.path}: {i.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function SaveButton({
  label,
  confirm,
  disabled,
  onSave,
}: {
  label: string;
  confirm: boolean;
  disabled: boolean;
  onSave: () => void;
}) {
  if (confirm)
    return (
      <ConfirmButton
        className="s-btn s-btn-tall s-btn-danger"
        label={label}
        confirmLabel="Remove them? Every version is kept."
        disabled={disabled}
        onConfirm={onSave}
      />
    );
  return (
    <button type="button" className="s-btn s-btn-tall" disabled={disabled} onClick={onSave}>
      {label}
    </button>
  );
}

/** Planned minutes against the talk's length, with how far off they are. */
function MinutesMeter({ planned, target }: { planned: number; target: number | undefined }) {
  if (!target) return <span className="col-num">{planned} min planned</span>;
  const off = planned - target;
  const pct = Math.min(100, (planned / target) * 100);
  return (
    <span className="meter" title={`${planned} of ${target} minutes planned`}>
      <span className="col-num">
        {planned} of {target} min{off > 0 ? ` · ${off} over` : off < 0 ? ` · ${-off} to plan` : ""}
      </span>
      <span className="meter-track" aria-hidden="true">
        <span className="meter-fill" data-over={off > 0} style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
}

/** Up to four key points, one per line. Keeps the raw text while typing. */
function PointsField({ label, value, onChange }: { label: string; value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState(value.join("\n"));
  const lines = text.split("\n");
  const over = lines.filter((l) => l.trim()).length > 4 || lines.some((l) => l.trim().length > 60);
  return (
    <>
      <textarea
        className="textarea outline-points"
        aria-label={label}
        rows={Math.max(2, Math.min(4, lines.length))}
        placeholder={"Key points, one per line (up to 4, shown on the section slide)"}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(
            e.target.value
              .split("\n")
              .map((l) => l.trim().slice(0, 60))
              .filter(Boolean)
              .slice(0, 4),
          );
        }}
      />
      {over && <span className="field-hint">Up to 4 points of 60 characters; the rest isn’t kept.</span>}
    </>
  );
}
